import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Stars from "@/components/Stars";
import { Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const Reviews = ({ productId }) => {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", rating: 5, title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/products/${productId}/reviews`);
      setData(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please add your name");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, {
        name: form.name.trim(),
        rating: Number(form.rating),
        title: form.title.trim(),
        body: form.body.trim(),
      });
      toast.success("Thanks for the review — your voice matters.");
      setForm({ name: "", rating: 5, title: "", body: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center" data-testid="reviews-loading">
        <Loader2 className="animate-spin text-zinc-500" size={24} />
      </div>
    );
  }

  return (
    <section
      className="border-t border-zinc-900 pt-12 mt-16"
      data-testid="reviews-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Summary + form */}
        <div className="lg:col-span-1 space-y-6">
          <div>
            <h3 className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
              // Athlete Reviews
            </h3>
            <h2 className="font-heading text-4xl font-black uppercase tracking-tight">
              The Verdict
            </h2>
          </div>

          {data.count > 0 ? (
            <div className="space-y-2" data-testid="reviews-summary">
              <div className="flex items-baseline gap-3">
                <span className="font-heading text-5xl font-black metallic-text">
                  {data.average.toFixed(1)}
                </span>
                <span className="text-zinc-500 text-xs tracking-widest uppercase">
                  / 5
                </span>
              </div>
              <Stars rating={data.average} size={18} />
              <p className="text-xs text-zinc-500 tracking-widest uppercase">
                Based on {data.count} review{data.count !== 1 ? "s" : ""}
              </p>
            </div>
          ) : (
            <p className="text-sm text-zinc-500" data-testid="reviews-empty">
              Be the first to review this gear.
            </p>
          )}

          <form
            onSubmit={submit}
            className="space-y-3 border border-zinc-800 bg-zinc-950 p-5"
            data-testid="review-form"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Leave a Review
            </div>

            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  className={
                    n <= form.rating ? "text-amber-400" : "text-zinc-700"
                  }
                  aria-label={`${n} stars`}
                  data-testid={`review-star-${n}`}
                >
                  <Star
                    size={22}
                    fill={n <= form.rating ? "currentColor" : "none"}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            <input
              required
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-zinc-500"
              data-testid="review-name-input"
            />
            <input
              placeholder="Headline (optional)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-zinc-500"
              data-testid="review-title-input"
            />
            <textarea
              rows="3"
              placeholder="Tell us how it fits, performs, holds up..."
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-zinc-500"
              data-testid="review-body-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-white text-black font-heading font-bold text-xs tracking-widest uppercase py-3 hover:bg-zinc-300 disabled:opacity-50"
              data-testid="review-submit-btn"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        </div>

        {/* List */}
        <div className="lg:col-span-2 space-y-6">
          {data.reviews.length === 0 ? (
            <p className="text-zinc-600 text-sm">
              No reviews yet. The mat is open.
            </p>
          ) : (
            data.reviews.map((r) => (
              <div
                key={r.id}
                className="border-b border-zinc-900 pb-6"
                data-testid={`review-${r.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm tracking-wide">
                        {r.name}
                      </span>
                      {r.verified && (
                        <span className="text-[9px] tracking-widest uppercase bg-emerald-900/40 text-emerald-400 px-2 py-0.5">
                          Verified
                        </span>
                      )}
                    </div>
                    <Stars rating={r.rating} size={12} className="mt-1" />
                  </div>
                  <span className="text-[10px] tracking-widest uppercase text-zinc-600">
                    {new Date(r.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                {r.title && (
                  <h4 className="font-bold text-base uppercase tracking-wide mb-1">
                    {r.title}
                  </h4>
                )}
                {r.body && (
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {r.body}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
